/**
 * @fileoverview ProductMediaModalV2 custom element.
 * Provides a fullscreen modal containing a Swiper-based media gallery.
 * Implements the `.show(opener)` interface compatible with Shopify's <modal-opener>.
 */

if (!customElements.get("product-media-modal-v2")) {
  /**
   * @class ProductMediaModalV2
   * @extends HTMLElement
   * @description Handles the lifecycle, asset lazy-loading, and Swiper initialization for the product media modal.
   */
  class ProductMediaModalV2 extends HTMLElement {
    constructor() {
      super();
      /** @type {import('swiper').Swiper | null} Main Swiper instance */
      this.mainSwiper = null;
      /** @type {import('swiper').Swiper | null} Thumbs Swiper instance */
      this.thumbsSwiper = null;
      /** @type {HTMLElement | null} Element that opened the modal, used to restore focus on close */
      this.openedBy = null;
      /** @type {boolean} Tracks whether Swiper has been successfully initialized */
      this.initialized = false;
      /** @type {boolean} Tracks if the element has been moved to the document body */
      this.moved = false;
      /** @type {MediaQueryList} Desktop layout breakpoint for stacked modal media */
      this.desktopMediaQuery = window.matchMedia("(min-width: 750px)");
    }

    /**
     * @lifecycle
     * Fires when the element is inserted into the DOM.
     * Moves the modal to the body to prevent stacking context issues and sets up event listeners.
     */
    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      
      try {
        // Move to body to avoid z-index/overflow clipping issues from parent containers
        document.body.appendChild(this);
      } catch (error) {
        console.error("[ProductMediaModalV2] Failed to move modal to document body:", error);
      }

      const closeBtn = this.querySelector('[data-component="close"]');
      if (closeBtn) {
        closeBtn.addEventListener("click", () => this.hide());
      } else {
        console.warn("[ProductMediaModalV2] Close button not found. Expected element with [data-component='close'].");
      }

      this.addEventListener("keyup", (event) => {
        if (event.code === "Escape") this.hide();
      });

      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          event.target === this.querySelector('[data-component="overlay"]')
        ) {
          this.hide();
        }
      });
    }

    /**
     * @method show
     * @description Opens the modal, lazy loads Swiper assets, initializes the slider, and jumps to the requested media.
     * @param {HTMLElement} [opener] - The button/element that triggered the modal. Should contain a `data-media-id` attribute.
     */
    async show(opener) {
      try {
        this.openedBy = opener;
        document.body.classList.add("overflow-hidden");
        this.setAttribute("open", "");

        const mediaId = opener?.getAttribute("data-media-id");
        if (this.isDesktopStacked()) {
          this.destroySwiper();
          this.scrollToMedia(mediaId);
        } else {
          await this.initSwiper();
        }

        if (opener) {
          if (mediaId && !this.isDesktopStacked()) {
            this.slideToMedia(mediaId);
          }
        }

        if (typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia();
        }
      } catch (error) {
        console.error("[ProductMediaModalV2] Error during modal show sequence:", error);
      }
    }

    /**
     * @method hide
     * @description Closes the modal, restores body scroll, and returns focus to the opener element.
     */
    hide() {
      try {
        document.body.classList.remove("overflow-hidden");
        document.body.dispatchEvent(new CustomEvent("modalClosed"));
        this.removeAttribute("open");

        if (this.openedBy) {
          this.openedBy.focus();
          this.openedBy = null;
        }

        if (typeof window.pauseAllMedia === "function") {
          window.pauseAllMedia();
        }
      } catch (error) {
        console.error("[ProductMediaModalV2] Error during modal hide sequence:", error);
      }
    }

    /**
     * @method slideToMedia
     * @description Navigates the main Swiper to the slide matching the provided media ID.
     * @param {string|number} mediaId - The ID of the media to navigate to.
     */
    slideToMedia(mediaId) {
      if (!this.mainSwiper) {
        console.warn("[ProductMediaModalV2] Cannot slide to media: main Swiper is not initialized.");
        return;
      }

      try {
        const mainSliderEl = this.querySelector('[data-component="main-slider"]');
        if (!mainSliderEl) throw new Error("Main slider DOM element missing.");

        const slides = Array.from(mainSliderEl.querySelectorAll(".swiper-slide") || []);
        const targetIndex = slides.findIndex(
          (slide) => slide.getAttribute("data-media-id") === String(mediaId)
        );

        if (targetIndex >= 0) {
          this.mainSwiper.slideTo(targetIndex, 0);
        } else {
          console.warn(`[ProductMediaModalV2] Slide with media ID ${mediaId} not found.`);
        }
      } catch (error) {
        console.error("[ProductMediaModalV2] Error sliding to media:", error);
      }
    }

    /**
     * @method scrollToMedia
     * @description Scrolls the stacked desktop modal to the slide matching the provided media ID.
     * @param {string|number|null} mediaId - The ID of the media to scroll to.
     */
    scrollToMedia(mediaId) {
      try {
        const mainWrapperEl = this.querySelector(".product-media-modal-v2__main-wrapper");
        const mainSliderEl = this.querySelector('[data-component="main-slider"]');
        if (!mainWrapperEl || !mainSliderEl) return;

        const slides = Array.from(mainSliderEl.querySelectorAll(".product-media-modal-v2__slide") || []);
        const targetSlide = mediaId
          ? slides.find((slide) => slide.getAttribute("data-media-id") === String(mediaId))
          : slides[0];

        window.requestAnimationFrame(() => {
          mainWrapperEl.scrollTo({
            top: targetSlide ? targetSlide.offsetTop : 0,
            behavior: "auto",
          });
        });
      } catch (error) {
        console.error("[ProductMediaModalV2] Error scrolling to media:", error);
      }
    }

    /**
     * @method isDesktopStacked
     * @description Returns true when the modal should use the desktop stacked layout instead of Swiper.
     */
    isDesktopStacked() {
      return this.desktopMediaQuery.matches;
    }

    /**
     * @method initSwiper
     * @description Lazy loads Swiper JS/CSS and initializes the thumbs and main slider instances.
     */
    async initSwiper() {
      if (this.mainSwiper || this.initialized) return;

      try {
        await this.loadAssets();
      } catch (error) {
        console.error("[ProductMediaModalV2] Failed to load Swiper assets. Ensure data-swiper-js and data-swiper-css are provided.", error);
        return;
      }

      if (!window.Swiper) {
        console.error("[ProductMediaModalV2] Swiper constructor is not available on the window object after asset load.");
        return;
      }

      try {
        const thumbsEl = this.querySelector('[data-component="thumbs-slider"]');
        const mainEl = this.querySelector('[data-component="main-slider"]');
        
        if (!mainEl) {
          throw new Error("Missing required element with [data-component='main-slider'].");
        }

        // Initialize Thumbs Swiper (must happen before main Swiper)
        if (thumbsEl) {
          this.thumbsSwiper = new window.Swiper(thumbsEl, {
            slidesPerView: 5,
            spaceBetween: 9,
            watchSlidesProgress: true,
            breakpoints: {
              750: {
                slidesPerView: 8,
                spaceBetween: 10,
              },
            },
          });
        }

        // Initialize Main Swiper
        const mainOpts = {
          slidesPerView: 1,
          spaceBetween: 0,
          autoHeight: true,
          navigation: {
            prevEl: this.querySelector('[data-component="nav-prev"]'),
            nextEl: this.querySelector('[data-component="nav-next"]'),
          },
        };

        if (this.thumbsSwiper) {
          mainOpts.thumbs = { swiper: this.thumbsSwiper };
        }

        this.mainSwiper = new window.Swiper(mainEl, mainOpts);
        this.initialized = true;

      } catch (error) {
        console.error("[ProductMediaModalV2] Error initializing Swiper instances:", error);
      }
    }

    /**
     * @method destroySwiper
     * @description Destroys Swiper instances so desktop stacked layout can use normal document flow.
     */
    destroySwiper() {
      try {
        if (this.mainSwiper) {
          this.mainSwiper.destroy(true, true);
          this.mainSwiper = null;
        }
        if (this.thumbsSwiper) {
          this.thumbsSwiper.destroy(true, true);
          this.thumbsSwiper = null;
        }
        this.initialized = false;
      } catch (error) {
        console.error("[ProductMediaModalV2] Error destroying Swiper instances:", error);
      }
    }

    /**
     * @lifecycle
     * @description Cleans up Swiper instances when the element is removed from the DOM.
     */
    disconnectedCallback() {
      this.destroySwiper();
    }

    /**
     * @method loadAssets
     * @description Ensures Swiper CSS and JS are loaded into the document exactly once.
     * @returns {Promise<any>}
     */
    async loadAssets() {
      if (window.Swiper) return;

      if (window.__productMediaModalV2SwiperPromise) {
        return window.__productMediaModalV2SwiperPromise;
      }

      const swiperJS = this.getAttribute("data-swiper-js");
      const swiperCSS = this.getAttribute("data-swiper-css");

      if (!swiperJS || !swiperCSS) {
        throw new Error("Missing Swiper asset URLs in element attributes (data-swiper-js, data-swiper-css).");
      }

      window.__productMediaModalV2SwiperPromise = Promise.all([
        this.loadStylesheet(swiperCSS),
        this.loadScript(swiperJS),
      ]);

      return window.__productMediaModalV2SwiperPromise;
    }

    /**
     * @method loadStylesheet
     * @description Injects a CSS link into the document head safely.
     * @param {string} href 
     * @returns {Promise<boolean>}
     */
    loadStylesheet(href) {
      return new Promise((resolve) => {
        if (!href) return resolve(false);
        const existing = document.querySelector(`link[href="${href}"]`);
        if (existing) return resolve(true);

        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        link.onload = () => resolve(true);
        link.onerror = () => {
          console.error(`[ProductMediaModalV2] Failed to load stylesheet: ${href}`);
          resolve(false);
        };
        document.head.appendChild(link);
      });
    }

    /**
     * @method loadScript
     * @description Injects a JS script into the document head safely.
     * @param {string} src 
     * @returns {Promise<boolean>}
     */
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (!src) return reject(new Error("Missing script URL."));

        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          if (existing.dataset.loaded === "true") return resolve(true);
          existing.addEventListener("load", () => resolve(true), { once: true });
          existing.addEventListener(
            "error",
            () => reject(new Error(`[ProductMediaModalV2] Script load failed: ${src}`)),
            { once: true }
          );
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        
        script.addEventListener("load", () => {
            script.dataset.loaded = "true";
            resolve(true);
        }, { once: true });

        script.addEventListener("error", () => {
            reject(new Error(`[ProductMediaModalV2] Script load failed: ${src}`));
        }, { once: true });

        document.head.appendChild(script);
      });
    }
  }

  customElements.define("product-media-modal-v2", ProductMediaModalV2);
}
